/**
 * Bridge configuration types and API client.
 *
 * The bridge config is fetched from the API server (server-managed verified
 * configuration).  The frontend never supplies or trusts bridge contract
 * addresses from the browser.
 *
 * Security: all addresses come exclusively from the server, which reads them
 * from environment variables that were set by a human operator after verifying
 * them against official Robinhood Chain documentation.
 */

// ── Status machine ─────────────────────────────────────────────────────────────

export type BridgeStatus =
  | "idle"
  | "loading_config"
  | "preparing"
  | "awaiting_approval"
  | "submitted_l1"
  | "confirming_l1"
  | "depositing_l2"
  | "ready"
  | "failed"
  | "manual_redemption";

export const BRIDGE_STATUS_LABELS: Record<BridgeStatus, string> = {
  idle:              "Ready",
  loading_config:    "Preparing",
  preparing:         "Preparing",
  awaiting_approval: "Awaiting wallet approval",
  submitted_l1:      "Submitted on Ethereum",
  confirming_l1:     "Confirming",
  depositing_l2:     "Depositing to Robinhood Chain",
  ready:             "Ready",
  failed:            "Failed",
  manual_redemption: "Manual redemption available",
};

/** Ordered steps shown in the progress bar (excluding idle/loading). */
export const BRIDGE_STATUS_STEPS: BridgeStatus[] = [
  "preparing",
  "awaiting_approval",
  "submitted_l1",
  "confirming_l1",
  "depositing_l2",
  "ready",
];

// ── Server config shape ────────────────────────────────────────────────────────

export interface BridgeChainConfig {
  destinationChainId: number;
  destinationChainName: string;
  l1ChainId: number;
  l1ChainName: string;
  /** Canonical Arbitrum Inbox address on L1 — verified server-side. */
  inboxAddress: `0x${string}`;
  /** depositEth() function selector — 0x439370b1 */
  depositEthSelector: string;
  estimatedDepositSeconds: number;
  withdrawalChallengeSeconds: number;
  /** Minimum bridgeable amount in wei (decimal string). */
  minimumAmountWei: string;
}

// ── Persisted transaction ──────────────────────────────────────────────────────

export interface BridgeTransaction {
  id: string;
  wallet: string;
  destinationChainId: number;
  l1ChainId: number;
  l1TxHash: `0x${string}` | null;
  amountWei: string;
  status: BridgeStatus;
  retryableTicketId: string | null;
  returnContext: "trade" | "create" | null;
}

// ── API helpers ────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

function bridgeApiUrl(path: string): string {
  return `${API_BASE}/api/bridge${path}`;
}

/** Fetch the server-managed bridge configuration for a destination chain. */
export async function fetchBridgeConfig(destinationChainId: number): Promise<BridgeChainConfig> {
  const res = await fetch(bridgeApiUrl(`/config?chainId=${destinationChainId}`));
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
    throw new Error(body.message ?? body.error ?? `Bridge config unavailable (${res.status})`);
  }
  return res.json() as Promise<BridgeChainConfig>;
}

/** Persist bridge transaction progress on the server. */
export async function persistBridgeTx(tx: BridgeTransaction): Promise<void> {
  await fetch(bridgeApiUrl("/persist"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(tx),
  });
  // Best-effort — do not throw on persist failure; UX state is in memory
}

/** Fetch the most recent in-progress bridge transaction for a wallet. */
export async function fetchPendingBridgeTx(
  wallet: string,
  destinationChainId: number,
): Promise<BridgeTransaction | null> {
  const res = await fetch(
    bridgeApiUrl(`/pending?wallet=${encodeURIComponent(wallet)}&chainId=${destinationChainId}`),
  );
  if (!res.ok) return null;
  const body = await res.json() as { pending: BridgeTransaction | null };
  return body.pending ?? null;
}
